import React, { useState } from 'react'

import { Icon, Modal, Popover } from '@clientbase/clientbase-library'
import { IconButton, Tooltip } from '@mui/material'
import { usePermissionsContext } from 'context/PermissionsContext'
import { useCreationBlocked } from 'hooks/useCreationBlocked'
import { isGroupMode } from 'lib/merchant-group'
import { useRouter } from 'next/router'
import { darken } from 'polished'
import { AccountsPayableProvider } from 'templates/AccountsPayable/context'
import FormProducts from 'templates/v2/Products/Form/FormProducts'

import { FormCompanyScope } from 'components/CompanySelector'
import ModalNewCharge from 'components/Modal/ModalNewCharge'
import ModalNewCustomer from 'components/Modal/ModalNewCustomer'
import ModalNewExpense from 'components/Modal/ModalNewExpense'
import ModalNewPaymentLink from 'components/Modal/ModalNewPaymentLink/components'
import ModalNewRecurrence from 'components/Modal/ModalNewRecurrence'

import getCurrentMonth from 'utils/get-current-month'
import { FeatureTabs } from 'utils/tabs'

import { theme } from 'styles/theme'

import { TOPBAR_EVENTS, trackTopbar } from '../../topbarTracking'
import {
  QuickActionButtonProps,
  QuickActionType
} from './QuickActionButton.interface'
import * as S from './QuickActionButton.styles'

// Nome do recurso exibido no modal de upgrade, por ação do atalho global.
const QUICK_ACTION_RESOURCE_NAMES: Record<QuickActionType, string> = {
  customer: 'Clientes',
  charge: 'Cobranças',
  recurrence: 'Recorrências',
  product: 'Planos e produtos',
  payment_link: 'Links de contratação',
  contract: 'Contratos',
  expense: 'Contas a pagar'
}

// Permissão RBAC exigida por atalho — espelha o `*#create` correspondente em
// api/app/lib/permissions/route_permissions.rb, que é quem barra de verdade.
const QUICK_ACTION_PERMISSIONS: Record<QuickActionType, string> = {
  customer: 'customers:manage',
  charge: 'billings:manage',
  recurrence: 'recurrences:manage',
  product: 'products:manage',
  payment_link: 'product_links:manage',
  contract: 'contracts:manage',
  expense: 'accounts_payable:manage'
}

interface ExpenseModalWrapperProps {
  onClose: (uuid?: string) => void
}

const ExpenseModalWrapper: React.FC<ExpenseModalWrapperProps> = ({
  onClose
}) => {
  // SC-10999: na visão multi-empresas a despesa é lançada na empresa
  // escolhida no topo do formulário
  return (
    <FormCompanyScope>
      {(companySelector) => (
        <ModalNewExpense
          isOpen={true}
          companySelector={companySelector}
          handleClose={() => {
            onClose()
          }}
          onSuccess={(uuid) => {
            onClose(uuid)
          }}
        />
      )}
    </FormCompanyScope>
  )
}

// Nome legível de cada opção do "+" para os eventos do Mixpanel (SC-11888).
const QUICK_ACTION_LABELS: Record<QuickActionType, string> = {
  customer: 'Cliente',
  contract: 'Contrato',
  charge: 'Cobrança',
  recurrence: 'Recorrência',
  expense: 'Despesa',
  product: 'Plano',
  payment_link: 'Link de Contratação'
}

const QuickActionButton: React.FC<QuickActionButtonProps> = () => {
  const router = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openModal, setOpenModal] = useState<QuickActionType | null>(null)
  const { creationBlocked, openUpgradeModal } = useCreationBlocked()
  const { permissions, hasPermission } = usePermissionsContext()

  const handleTogglePopover = (event: React.MouseEvent<HTMLElement> | null) => {
    if (event && !anchorEl) trackTopbar(TOPBAR_EVENTS.createOpened)
    setAnchorEl(event ? event.currentTarget : null)
  }

  const handleOpenModal = (type: QuickActionType) => {
    trackTopbar(TOPBAR_EVENTS.used, {
      tipo: 'criar',
      atalho: QUICK_ACTION_LABELS[type],
      bloqueado_por_plano: creationBlocked && !isGroupMode()
    })

    // Plano Free / trial expirado: o atalho global de criação abre o modal de
    // upgrade em vez da ação (SC-10363).
    // SC-10999: no grupo o bloqueio que vale é o da empresa escolhida no
    // formulário (CreateGate dentro dele), não o da empresa de contexto
    if (creationBlocked && !isGroupMode()) {
      handleTogglePopover(null)
      openUpgradeModal(QUICK_ACTION_RESOURCE_NAMES[type])
      return
    }

    if (type === 'contract') {
      handleTogglePopover(null)
      router.push('/contratos/novo-contrato')
      return
    }
    setOpenModal(type)
    handleTogglePopover(null)
  }

  const handleCloseModal = () => {
    setOpenModal(null)
  }

  const handleRedirectAfterSuccess = (
    type: QuickActionType,
    uuid?: string,
    totalCycles?: number
  ) => {
    trackTopbar(TOPBAR_EVENTS.createCompleted, {
      atalho: QUICK_ACTION_LABELS[type]
    })
    const currentMonth = getCurrentMonth().join('|')

    const routes: Record<QuickActionType, string> = {
      customer: `/clientes?tab=${FeatureTabs.CUSTOMERS}`,
      product: uuid
        ? `/planos/${uuid}`
        : `/comercial?tab=${FeatureTabs.PRODUCTS}`,
      payment_link: `/comercial?tab=${FeatureTabs.PAYMENT_LINK}${
        uuid ? `&payment_link_uuid=${uuid}` : ''
      }`,
      contract: `/comercial?tab=${FeatureTabs.CONTRACTS}`,
      charge: `/cobrancas?tab=${
        FeatureTabs.CHARGES
      }&due_date_between=${currentMonth}${
        uuid ? `&charge_uuid=${uuid}&total_cycles=${totalCycles || 1}` : ''
      }`,
      recurrence: `/cobrancas?tab=${FeatureTabs.RECURRENCES}${
        uuid ? `&recurrence_uuid=${uuid}&total_cycles=${totalCycles || 2}` : ''
      }`,
      expense: `/operacao?tab=${
        FeatureTabs.ACCOUNTS_PAYABLE
      }&due_date_between=${currentMonth}${uuid ? `&expense_uuid=${uuid}` : ''}`
    }

    router.push(routes[type])
  }

  const menuOptions = [
    {
      type: 'customer' as QuickActionType,
      icon: 'person',
      label: 'Cliente',
      inactive: false
    },
    {
      type: 'contract' as QuickActionType,
      icon: 'assignment',
      label: 'Contrato',
      inactive: false
    },
    {
      type: 'charge' as QuickActionType,
      icon: 'receipt',
      label: 'Cobrança',
      inactive: false
    },
    {
      type: 'recurrence' as QuickActionType,
      icon: 'cached',
      label: 'Recorrência',
      inactive: true
    },
    {
      type: 'expense' as QuickActionType,
      icon: 'faCoins',
      label: 'Despesa',
      inactive: false
    },
    {
      type: 'product' as QuickActionType,
      icon: 'faStar',
      label: 'Plano',
      inactive: true
    },
    {
      type: 'payment_link' as QuickActionType,
      icon: 'faCreditCard',
      label: 'Link de Contratação',
      inactive: true
    }
  ]

  const availableOptions = menuOptions.filter(
    (option) =>
      !option.inactive && hasPermission(QUICK_ACTION_PERMISSIONS[option.type])
  )

  // Sem nenhuma ação permitida o atalho vira um popover vazio — melhor sumir.
  // Só decide depois que o bitmap hidrata: no primeiro render `permissions`
  // ainda está vazio e esconder ali daria um flash do botão no AppBar.
  if (permissions.length > 0 && availableOptions.length === 0) return null

  return (
    <>
      <Tooltip title="Criar">
        <IconButton onClick={handleTogglePopover}>
          <Icon
            icon="faCirclePlus"
            colorSX={darken(0.05, theme.palette.primary.main)}
            height="21px"
            width="21px"
          />
        </IconButton>
      </Tooltip>

      <Popover
        onClick={() => handleTogglePopover(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
        keepMounted={false}
        onClose={() => handleTogglePopover(null)}
        open={Boolean(anchorEl)}
        sx={{
          maxWidth: '950px',
          minWidth: '300px'
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
      >
        <S.MenuItemsWrapper>
          {availableOptions.map((option) => (
            <S.MenuItem
              key={option.type}
              onClick={() => handleOpenModal(option.type)}
            >
              <Icon
                icon={option.icon as any}
                colorSX={darken(0.05, theme.palette.primary.main)}
                size={2.5}
              />
              {option.label}
            </S.MenuItem>
          ))}
        </S.MenuItemsWrapper>
      </Popover>

      {/* Modal Cliente */}
      {openModal === 'customer' && (
        <ModalNewCustomer
          isOpenModalNewCustomer={true}
          setIsOpenModalNewCustomer={(isOpen) => {
            if (!isOpen) {
              handleCloseModal()
            }
          }}
        />
      )}

      {/* Modal Cobrança */}
      {openModal === 'charge' && (
        <ModalNewCharge
          isOpenModalNewCharge={true}
          onClose={(refresh) => {
            handleCloseModal()
            if (refresh) {
              handleRedirectAfterSuccess('charge')
            }
          }}
          handleCloseModal={() => {
            handleCloseModal()
          }}
          handleOpenDetailChargeOrRecurrence={({ uuid, totalCycles }) => {
            handleCloseModal()
            handleRedirectAfterSuccess('charge', uuid, totalCycles)
          }}
        />
      )}

      {/* Modal Recorrência */}
      {openModal === 'recurrence' && (
        <ModalNewRecurrence
          isOpenModalNewRecurrence={true}
          onClose={(refresh) => {
            handleCloseModal()
            if (refresh) {
              handleRedirectAfterSuccess('recurrence')
            }
          }}
          handleCloseModal={() => {
            handleCloseModal()
          }}
          handleOpenDetailChargeOrRecurrence={({ uuid, totalCycles }) => {
            handleCloseModal()
            handleRedirectAfterSuccess('recurrence', uuid, totalCycles)
          }}
        />
      )}

      {/* Modal Plano */}
      {openModal === 'product' && (
        <Modal
          handleClose={() => {
            handleCloseModal()
          }}
          open={true}
          width="100%"
          maxWidth="85vw"
          maxHeight="90vh"
          height="100%"
        >
          <FormProducts
            handleRefreshPlans={() => {
              handleCloseModal()
            }}
            setIsOpenDialog={() => {
              handleCloseModal()
            }}
            onProductCreated={(uuid) => {
              handleCloseModal()
              handleRedirectAfterSuccess('product', uuid)
            }}
          />
        </Modal>
      )}

      {/* Modal Link de Contratação */}
      {openModal === 'payment_link' && (
        <ModalNewPaymentLink
          isOpen={true}
          handleClose={(refresh?: boolean, newPaymentLink?: any) => {
            handleCloseModal()
            if (refresh && newPaymentLink?.uuid) {
              handleRedirectAfterSuccess('payment_link', newPaymentLink.uuid)
            }
          }}
          isEdit={false}
          isDuplication={false}
        />
      )}

      {/* Modal Despesa */}
      {openModal === 'expense' && (
        <AccountsPayableProvider>
          <ExpenseModalWrapper
            onClose={(uuid?: string) => {
              handleCloseModal()
              if (uuid) {
                handleRedirectAfterSuccess('expense', uuid)
              }
            }}
          />
        </AccountsPayableProvider>
      )}
    </>
  )
}

export default QuickActionButton
